import { type ReactElement, useEffect } from 'react';
import { type GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import useSWR from 'swr';
import { Container, Paper } from '@mui/material';

import Preloader from '../components/preloader';
import SeoTags from '../components/seo-tags';
import ParallaxBackground from '../components/parallax-background';
import { withStrapi } from '../utils/api/Strapi';

const HTMLParser = dynamic(() => import('../components/html-parser'));

export default function NotFoundPage({ errorMessage }: PageProps): ReactElement {
  const aboutPage = useSWR<StaticPageProps>(!errorMessage ? '/api/v1/not-found-page?populate=*' : null);
  const { data, isLoading, isValidating } = aboutPage;

  useEffect(() => {
    if (!data && !isLoading && !isValidating) {
      throw new Error(errorMessage);
    }
  }, [aboutPage]);

  if (isLoading || isValidating) {
    return <Preloader />;
  }

  if (!data) {
    return <Preloader />;
  }

  return (
    <>
      <SeoTags {...data.seo} extraStyles={['/assets/chart-types/sprite.css']} />
      <ParallaxBackground>
        <Container component="main" maxWidth="md" sx={{ placeContent: 'center' }}>
          <Paper sx={{ p: 4 }}>
            <HTMLParser content={data.content} />
          </Paper>
        </Container>
      </ParallaxBackground>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<GetServerSidePropsResult<StaticPageProps>> = withStrapi(
  async ({ api }) => {
    const { user } = api;

    const { data } = await api.request<{ data: StaticPageProps }>('GET', 'not-found-page?populate=*');

    return {
      props: {
        fallback: {
          '/api/v1/auth/me': user,
          '/api/v1/not-found-page?populate=*': data,
        },
      },
    };
  },
);
